import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, Tab, Tabs, Typography } from '@mui/material';
import StaffingLeaveApplyPanel from './StaffingLeaveApplyPanel';
import StaffingPayslipPanel from './StaffingPayslipPanel';
import StaffingHolidayCalendar from './StaffingHolidayCalendar';
import StaffingPolicyPdfView from './StaffingPolicyPdfView';
import StaffingGrievancePanel from './StaffingGrievancePanel';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function StaffingEmployeeHub({ token, onGoAttendance }) {
  const [ctx, setCtx] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token || !API_URL) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/candidate/staffing/context`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCtx(data.hasStaffing ? data : null);
    } catch {
      setCtx(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return null;
  if (!ctx) return null;

  return (
    <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #99f6e4' }}>
      <CardContent>
        <Typography variant="h6" fontWeight={800} gutterBottom>
          Staffing — {ctx.company_name}
        </Typography>
        {!ctx.active && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Status: <strong>{ctx.status}</strong>. Complete LOI acceptance, then check in on or after your joining date (
            {ctx.joining_date || 'set by employer'}) to become active — or ask HR to activate you.{' '}
            {onGoAttendance && (
              <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={onGoAttendance}>
                Go to Attendance
              </Box>
            )}
          </Alert>
        )}
        {ctx.active && (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
              <Tab label="Holiday calendar" />
              <Tab label="Leave & outdoor" />
              <Tab label="Payslips" />
              <Tab label="Policies" />
              <Tab label="Grievance" />
              <Tab label="My status" />
            </Tabs>
            {tab === 0 && <StaffingHolidayCalendar token={token} />}
            {tab === 1 && <StaffingLeaveApplyPanel token={token} embedded />}
            {tab === 2 && <StaffingPayslipPanel token={token} />}
            {tab === 3 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <StaffingPolicyPdfView token={token} documentMeta={ctx.hr_policy_document} title="HR policy" />
                <StaffingPolicyPdfView token={token} documentMeta={ctx.leave_policy_document} title="Leave policy" />
              </Box>
            )}
            {tab === 4 && <StaffingGrievancePanel token={token} embedded />}
            {tab === 5 && (
              <Box>
                <Typography variant="body2">Company: {ctx.company_name}</Typography>
                <Typography variant="body2">Manager: {ctx.manager_name || 'Not assigned'}</Typography>
                <Typography variant="body2">Joining: {ctx.joining_date || '—'}</Typography>
                <Typography variant="body2">Status: Active employee</Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
